import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { vendorAuthAPI } from '../../services/vendorApi';

const VendorLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const needsVendorAccount = location.state?.needsVendorAccount;
  
  const [step, setStep] = useState(1); // 1: mobile, 2: OTP, 3: name/email (for new vendors)
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isExistingVendor, setIsExistingVendor] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  /** Digits only, max 10 (Indian mobile without country code) */
  const handleMobileInputChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(digits);
  };

  const isMobileValid = mobile.length === 10;

  // Format Indian mobile number to international format
  const formatIndianMobile = (mobile) => {
    // Remove all non-digits
    const digits = mobile.replace(/\D/g, '');
    
    // If starts with 0, remove it
    const cleaned = digits.startsWith('0') ? digits.substring(1) : digits;
    
    // If starts with 91, use as is, otherwise add +91
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+${cleaned}`;
    } else if (cleaned.length === 10) {
      return `+91${cleaned}`;
    }
    
    return mobile; // Return as is if doesn't match expected format
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    
    if (mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');
      
      const formattedMobile = formatIndianMobile(mobile);
      const response = await vendorAuthAPI.sendOTP(formattedMobile);
      
      setIsExistingVendor(response.isExistingVendor);
      setOtpSent(true);
      setStep(2); // Move to OTP step
    } catch (error) {
      console.error('Send OTP error:', error);
      setError(error.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOTPSubmit = async () => {
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      const formattedMobile = formatIndianMobile(mobile);
      
      if (isExistingVendor) {
        // Existing vendor - just verify OTP and login
        const response = await vendorAuthAPI.verifyOTP(formattedMobile, otpString);
        login(response.user, response.token);
        navigate('/vendor/growth');
      } else {
        // New vendor - verify OTP first
        // Backend will validate OTP first, then check for name/email
        try {
          const response = await vendorAuthAPI.verifyOTP(formattedMobile, otpString, name, email);
          // If we get here, OTP was valid and account was created/logged in
          login(response.user, response.token);
          navigate('/vendor/growth');
        } catch (verifyError) {
          // Check if error code indicates OTP was valid but name/email missing
          const errorData = verifyError.response?.data;
          if (errorData?.code === 'MISSING_NAME_EMAIL' || 
              (errorData?.otpVerified === true && errorData?.error?.includes('Name and email'))) {
            // OTP was valid, but name/email missing - move to step 3
            setStep(3);
            setIsProcessing(false);
            return;
          } else {
            // OTP was invalid or other error - show error and stay on OTP step
            throw verifyError;
          }
        }
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Invalid OTP. Please try again.';
      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  const handleNameEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || name.trim().length < 2) {
      setError('Please enter a valid name');
      return;
    }
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      const formattedMobile = formatIndianMobile(mobile);
      const otpString = otp.join('');
      
      const response = await vendorAuthAPI.verifyOTP(formattedMobile, otpString, name, email);
      login(response.user, response.token);
      navigate('/vendor/growth');
    } catch (error) {
      console.error('Verify OTP error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#FF5E4C] rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">V</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Portal</h1>
          <p className="text-gray-600 mt-2">Sign in to manage your stays and events</p>
        </div>

        {needsVendorAccount && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-900">
              Sign in with your <strong>vendor mobile number</strong> (OTP). Guest accounts cannot access the vendor portal.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Step 1: Mobile Number */}
        {step === 1 && (
          <form onSubmit={handleMobileSubmit} className="space-y-4">
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                type="tel"
                id="mobile"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={10}
                value={mobile}
                onChange={handleMobileInputChange}
                placeholder="10-digit mobile number"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={isProcessing || !isMobileValid}
              className="w-full bg-[#FF5E4C] text-white py-3 rounded-lg font-medium hover:bg-[#FF4A36] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Enter OTP</h2>
              <p className="text-sm text-gray-600 mb-4">
                We've sent a 6-digit OTP to {mobile}
              </p>
              <div className="flex gap-2 justify-center">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOTPChange(index, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(index, e)}
                    className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent outline-none"
                  />
                ))}
              </div>
            </div>
            <button
              onClick={handleOTPSubmit}
              disabled={isProcessing || otp.join('').length !== 6}
              className="w-full bg-[#FF5E4C] text-white py-3 rounded-lg font-medium hover:bg-[#FF4A36] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button
              onClick={() => {
                setStep(1);
                setOtp(['', '', '', '', '', '']);
                setOtpSent(false);
                setError('');
              }}
              className="w-full text-[#FF5E4C] py-2 text-sm"
            >
              Change Mobile Number
            </button>
          </div>
        )}

        {/* Step 3: Name and Email (for new vendors) */}
        {step === 3 && (
          <form onSubmit={handleNameEmailSubmit} className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Complete Your Profile</h2>
              <p className="text-sm text-gray-600 mb-4">
                Please provide your details to complete registration
              </p>
            </div>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent outline-none"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent outline-none"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isProcessing}
              className="w-full bg-[#FF5E4C] text-white py-3 rounded-lg font-medium hover:bg-[#FF4A36] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Creating Account...' : 'Complete Registration'}
            </button>
            <button
              onClick={() => setStep(2)}
              className="w-full text-[#FF5E4C] py-2 text-sm"
            >
              Back to OTP
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default VendorLogin;

